import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import parsePhoneNumberFromString from 'libphonenumber-js';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router';

import {RolesAccount} from '@/app/auth';
import {COnboardingTypeStates} from '@/app/main/onboarding/constants/OnboardingConstants';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {sleep} from '@/app/utils/sleep';
import {useLoading} from '@/app/zustand/useLoading';
import {C_ORIGIN} from '@/constants/OriginConstants';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {useAmplifyAuth, useCountryCode} from '@/hooks';
import {
  AuthTypesFlow,
  CAwsAmplifyAuthChallenges,
  CONFIRM_SIGN_IN_CODE_CHALLENGES,
} from '@/services';
import logError from '@/utils/errors/logError';
import {generateUuid} from '@/utils/randomGenerator';

import {useVerifyAuthProcess} from '../../hooks/useVerifyAuthProcess';

import {
  defaultToast,
  delayAfterConfirmSignUp,
  errorMap,
  errorMessagesMap,
  maxLengthCode,
} from './constants';
import {useTrackers} from './trackers';
import {VerifyViewModelContextType} from './types';
import {useGetToken} from './useGetToken';
import {
  isAliasExistsException,
  isConfirmSignInAfterSignUpException,
} from './utils';

const VerifyViewModelContext = createContext<
  VerifyViewModelContextType | undefined
>(undefined);

export function VerifyViewModelProvider({children}: {children: ReactNode}) {
  const {profile} = useUsersRepository();
  const [showSendCode, setShowSendCode] = useState(false);
  const [smsCode, setSmsCode] = useState<string>();
  const [hasVerified, setHasVerified] = useState(false);
  const {isCountryCodeVisible} = useCountryCode();

  const {
    getToken: amplifyGetToken,
    handleSignInConfirmation,
    confirmSignUp,
    getCurrentSession,
    signIn,
    resendCodeSignUpConfirmation,
    isLoading: isLoadingAmplifyAuth,
  } = useAmplifyAuth();
  const location = useLocation();
  const {t} = useTranslation();
  const {startLoading, stopLoading} = useLoading();
  const {
    phone,
    sharedKey,
    isSignUp,
    firstName,
    lastName,
    userName,
    password,
    referral_code,
    invitation_code,
    email,
    role,
    marketing_opt_in,
    enableBankTesting,
  } = location.state || {};
  const navigate = useNavigate();
  const {verifyAuthProcess} = useVerifyAuthProcess();
  const {
    trackViewAuthEnterOTP,
    trackSelectAuthVerifyOTP,
    trackApiAuthVerifyOTPSuccess,
    trackApiAuthVerifyOTPError,
    trackApiAuthVerifyOTPNoCode,
    trackSelectAuthResendOTP,
    trackApiAuthResendOTPError,
  } = useTrackers();

  useEffect(() => {
    if (smsCode && smsCode.length === maxLengthCode && !hasVerified) {
      setHasVerified(true);
      handleVerify()
        .then(() => {})
        .catch(error => {
          logError(error, {
            fileName: 'VerifySms ViewModelContext',
            functionName: 'useEffect handleVerify',
            extraTags: {
              flow: isSignUp ? 'signup' : 'signin',
              'user.phone': phone,
              ...(email && {'user.email': email}),
            },
          });
          setHasVerified(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVerified, smsCode]);

  const handleOnBack = useCallback(() => {
    if (isSignUp) {
      const queryParams = new URLSearchParams({
        ...(invitation_code && {invitation_code: invitation_code}),
        ...(referral_code && {referral_code: referral_code}),
        ...(enableBankTesting !== undefined && {
          enable_bank_testing: String(enableBankTesting),
        }),
      });
      const signUpData = {
        firstName,
        lastName,
        phone,
        email,
      };
      navigate(`/authentication/sign-up?${queryParams.toString()}`, {
        state: signUpData,
      });
      return;
    }
    const signInData = {
      phone,
    };
    navigate('/authentication/sign-in', {
      state: signInData,
    });
  }, [
    email,
    enableBankTesting,
    firstName,
    invitation_code,
    isSignUp,
    lastName,
    navigate,
    phone,
    referral_code,
  ]);

  const {getToken} = useGetToken({
    phone,
    sharedKey,
    smsCode,
    setSmsCode,
    navigate,
    amplifyGetToken,
  });

  const handleSignInFlow = useCallback(
    async (codeSms: string, isFromSignIn: boolean) => {
      const result = await handleSignInConfirmation(codeSms, phone);
      if (result.nextStep.signInStep === CAwsAmplifyAuthChallenges.DONE) {
        const sessionResult = await getCurrentSession(true);
        if (sessionResult) {
          verifyAuthProcess(sessionResult, {
            referralCode: referral_code,
            invitationCode: invitation_code,
            flow: isSignUp ? C_ORIGIN.SIGNUP : C_ORIGIN.SIGNIN,
            phone,
            email,
          });
          trackApiAuthVerifyOTPSuccess(
            isFromSignIn,
            invitation_code,
            referral_code,
          );
        }
      }
    },
    [
      email,
      getCurrentSession,
      handleSignInConfirmation,
      invitation_code,
      isSignUp,
      phone,
      referral_code,
      trackApiAuthVerifyOTPSuccess,
      verifyAuthProcess,
    ],
  );

  const navigateToDownloadApp = useCallback(() => {
    navigate('/authentication/download-app');
  }, [navigate]);

  const handleSignUpFlow = useCallback(async () => {
    const signupData = {
      username: userName,
      confirmationCode: smsCode,
      first_name: firstName,
      last_name: lastName,
      referral_code: referral_code,
      invitation_code: invitation_code,
      shared_key: sharedKey,
      role: role,
      marketing_opt_in: marketing_opt_in,
      ...(enableBankTesting !== undefined && {enableBankTesting}),
    };
    const result = await confirmSignUp(signupData);
    if (result.nextStep.signUpStep === CAwsAmplifyAuthChallenges.DONE) {
      const inputSignIn = {
        password: password,
        username: userName,
      };
      const metadata = {
        shared_key: sharedKey,
        signup_login: 'true',
      };
      startLoading();
      await sleep(delayAfterConfirmSignUp);
      stopLoading();
      const resultSignIn = await signIn(
        inputSignIn,
        AuthTypesFlow.USER_SRP_AUTH,
        metadata,
      );
      if (
        CONFIRM_SIGN_IN_CODE_CHALLENGES.has(resultSignIn.nextStep.signInStep)
      ) {
        const sms = isSignUp ? await getToken(false) : smsCode;
        if (!sms || sms === '') {
          const origin = isSignUp ? C_ORIGIN.SIGNUP : C_ORIGIN.SIGNIN;
          logError(new Error('Failed to retrieve SMS code for verification'), {
            fileName: 'VerifySms ViewModelContext',
            functionName: 'handleSignUpFlow',
            extraTags: {
              flow: origin,
              'user.phone': phone,
              ...(email && {'user.email': email}),
            },
          });
          trackApiAuthVerifyOTPNoCode(origin, sharedKey, userName);
          navigateToDownloadApp();
          return;
        }
        await handleSignInFlow(sms, false);
      }
    }
  }, [
    confirmSignUp,
    email,
    enableBankTesting,
    firstName,
    getToken,
    handleSignInFlow,
    invitation_code,
    isSignUp,
    lastName,
    marketing_opt_in,
    navigateToDownloadApp,
    password,
    phone,
    referral_code,
    role,
    sharedKey,
    signIn,
    smsCode,
    startLoading,
    stopLoading,
    trackApiAuthVerifyOTPNoCode,
    userName,
  ]);

  const handleVerify = useCallback(async () => {
    try {
      trackSelectAuthVerifyOTP(isSignUp, referral_code, invitation_code);
      if (isSignUp) {
        await handleSignUpFlow();
      } else {
        await handleSignInFlow(smsCode, true);
      }
    } catch (error) {
      trackApiAuthVerifyOTPError(
        error,
        isSignUp ? C_ORIGIN.SIGNUP : C_ORIGIN.SIGNIN,
      );
      const isAliasExists = isAliasExistsException(error);
      const isConfirmSignInAfterSignUp =
        isConfirmSignInAfterSignUpException(error);
      // Do not report AliasExistsException to Sentry — it's an expected, handled case (phone already registered).
      if (!isAliasExists) {
        logError(error, {
          fileName: 'VerifySms ViewModelContext',
          functionName: 'handleVerify',
          extraTags: {
            flow: isSignUp ? 'signup' : 'signin',
            ...(phone && {'user.phone': phone}),
            ...(userName && {'user.username': userName}),
            ...(email && {'user.email': email}),
            ...(error?.code && {'error.code': error?.code}),
            ...(error?.name && {'error.name': error?.name}),
            ...(error?.message && {'error.message': error?.message}),
          },
        });
      }
      const errName = (error as {name?: string})?.name || '';
      const errMessage = (error as {message?: string})?.message || '';

      const {TITLE: nameSummary, DETAILS: nameDetail} =
        errorMap[errName] || defaultToast;

      const {TITLE: msgSummary, DETAILS: msgDetail} =
        errorMessagesMap[errMessage] || {};

      const summary = msgSummary || nameSummary;
      const detail = msgDetail || nameDetail;

      const redirectToDownloadApp = isAliasExists || isConfirmSignInAfterSignUp;

      const variant = redirectToDownloadApp
        ? SnackbarType.INFO
        : SnackbarType.ERROR;

      showSnackbar({
        summary: summary,
        message: detail,
        variant: variant,
      });

      if (redirectToDownloadApp) {
        navigateToDownloadApp();
      }
    }
  }, [
    email,
    handleSignInFlow,
    handleSignUpFlow,
    invitation_code,
    isSignUp,
    navigateToDownloadApp,
    phone,
    referral_code,
    smsCode,
    trackApiAuthVerifyOTPError,
    trackSelectAuthVerifyOTP,
    userName,
  ]);

  const onCountdownStopped = useCallback(() => {
    setShowSendCode(true);
  }, []);

  const showToastResendCode = useCallback(() => {
    setShowSendCode(false);
    showSnackbar({
      summary: t('verify_sms_page.sent_code.summary'),
      message: t('verify_sms_page.sent_code.message'),
      variant: SnackbarType.SUCCESS,
      autoHideDuration: 10000,
    });
  }, [t]);

  const handleErrorResendCode = useCallback(
    (error: any) => {
      trackApiAuthResendOTPError(error);
      logError(error, {
        fileName: 'VerifySms ViewModelContext',
        functionName: 'handleErrorResendCode',
        extraTags: {
          flow: isSignUp ? 'signup' : 'signin',
          'user.phone': phone,
          ...(email && {'user.email': email}),
        },
      });
      const errName = error?.name || '';
      const {TITLE: summary, DETAILS: detail} =
        errorMap[errName] || defaultToast;

      showSnackbar({
        summary: summary,
        message: detail,
      });
    },
    [email, isSignUp, phone, trackApiAuthResendOTPError],
  );

  const handleResendCodeSignUp = useCallback(async () => {
    try {
      await resendCodeSignUpConfirmation(userName);
      showToastResendCode();
    } catch (error) {
      handleErrorResendCode(error);
    }
  }, [
    handleErrorResendCode,
    resendCodeSignUpConfirmation,
    showToastResendCode,
    userName,
  ]);

  const handleResendCodeSignIn = useCallback(async () => {
    const inputSignIn = {
      password: password,
      username: phone,
    };
    const metadata = {
      shared_key: generateUuid(),
    };
    try {
      await signIn(inputSignIn, AuthTypesFlow.USER_SRP_AUTH, metadata);
      showToastResendCode();
    } catch (error) {
      handleErrorResendCode(error);
    }
  }, [handleErrorResendCode, password, phone, showToastResendCode, signIn]);

  const handleSendCode = useCallback(async () => {
    trackSelectAuthResendOTP(!isSignUp, invitation_code, referral_code);
    startLoading();
    if (isSignUp) {
      await handleResendCodeSignUp();
    } else {
      await handleResendCodeSignIn();
    }
    stopLoading();
  }, [
    handleResendCodeSignIn,
    handleResendCodeSignUp,
    invitation_code,
    isSignUp,
    referral_code,
    startLoading,
    stopLoading,
    trackSelectAuthResendOTP,
  ]);

  useEffect(() => {
    if (!profile) return;
    if (profile.role === RolesAccount.TEEN)
      return navigate('/teen/redirection');
    if (
      profile.role === RolesAccount.PARENT &&
      profile.onboarding === COnboardingTypeStates.COMPLETED
    ) {
      navigate('/onboarding/download-app?previousScreen=verify-sms');
    }
  }, [navigate, profile]);

  useEffect(() => {
    trackViewAuthEnterOTP(isSignUp, referral_code, invitation_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedPhone = useMemo(() => {
    if (!phone) return '';

    const phoneNumber = parsePhoneNumberFromString(phone, 'US');
    if (phoneNumber) {
      const nationalNumber = phoneNumber.nationalNumber;
      const formattedNational = `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
      return isCountryCodeVisible
        ? `+${phoneNumber.countryCallingCode} ${formattedNational}`
        : formattedNational;
    }

    return phone;
  }, [isCountryCodeVisible, phone]);

  const handleOnChangeSmsCode = useCallback(
    (value: string) => {
      setSmsCode(value?.trim());
    },
    [setSmsCode],
  );

  return (
    <VerifyViewModelContext.Provider
      value={{
        smsCode,
        formattedPhone,
        isSignUp,
        showSendCode,
        maxLengthCode,
        role,
        isLoadingAmplifyAuth,
        handleOnChangeSmsCode,
        onCountdownStopped,
        handleSendCode,
        handleVerify,
        handleOnBack,
        getToken,
      }}>
      {children}
    </VerifyViewModelContext.Provider>
  );
}

export function useVerifyViewModelProvider() {
  const context = useContext(VerifyViewModelContext);
  if (context === undefined) {
    throw new Error('VerifySMS ViewModelContext is undefined');
  }
  return context;
}
